import { KEY_USER_FINGERPRINT } from '@mygpt/app/apis';
import { getStorageValue, setStorageValue } from './storage';

// 生成SHA-1哈希
async function sha1(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-1', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  // 取前16位（8个字节）
  return hashArray
    .slice(0, 8)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// 获取更多浏览器特征信息
function getBrowserFeatures(): string[] {
  const features = [
    navigator.userAgent,
    navigator.language,
    `${window.screen.width}x${window.screen.height}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.hardwareConcurrency,
    (navigator as any).deviceMemory,
    Array.from(navigator.plugins)
      .map((p) => p.name)
      .join(','),
    // 添加更多特征
    navigator.platform,
    navigator.vendor,
    window.devicePixelRatio,
    navigator.maxTouchPoints,
    // 网络信息
    (navigator as any).connection?.effectiveType,
    (navigator as any).connection?.rtt,
    (navigator as any).connection?.downlink,
    // 添加Canvas指纹
    getCanvasFingerprint(),
    // 添加WebGL指纹
    getWebGLFingerprint(),
  ]
    .filter((feature) => feature !== undefined && feature !== null) // 只过滤掉 undefined 和 null，保留 0 和空字符串
    .map((feature) => String(feature)); // 转换为字符串，确保数字 0 变成 '0'

  return features;
}

// 获取Canvas指纹
function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    canvas.width = 200;
    canvas.height = 200;

    // 绘制一些文本和图形
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Canvas Fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Canvas Fingerprint', 4, 17);

    return canvas.toDataURL();
  } catch {
    return '';
  }
}

// 获取WebGL指纹
function getWebGLFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';

    const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return '';

    return (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string;
  } catch {
    return '';
  }
}

// 简单的字符串哈希算法（FNV-1a），用于降级场景
function simpleHash(str: string): string {
  let hash = 2166136261; // FNV offset basis
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }

  // 转换为16位十六进制字符串
  const hash32 = (hash >>> 0).toString(16).padStart(8, '0');
  // 使用字符串的另一部分再做一次哈希，生成另外8位
  let hash2 = 2166136261;
  for (let i = str.length - 1; i >= 0; i--) {
    hash2 ^= str.charCodeAt(i);
    hash2 += (hash2 << 1) + (hash2 << 4) + (hash2 << 7) + (hash2 << 8) + (hash2 << 24);
  }
  const hash32_2 = (hash2 >>> 0).toString(16).padStart(8, '0');

  return hash32 + hash32_2;
}

// 生成降级标识，基于浏览器特征（确保同一浏览器生成一致的ID）
function generateFallbackId(features: string[]): string {
  try {
    // 如果没有特征或特征为空，使用最小特征集
    if (!features || features.length === 0) {
      const minimalInfo = [
        navigator.userAgent || '',
        navigator.language || '',
        `${screen.width}x${screen.height}`,
      ].join('###');
      return simpleHash(minimalInfo);
    }

    // 组合所有特征
    const info = features.join('###');

    // 使用简单的哈希算法生成指纹
    return simpleHash(info);
  } catch {
    // 完全失败时，使用最小特征集作为最后兜底
    const minimalInfo = [navigator.userAgent || '', navigator.language || '', `${screen.width}x${screen.height}`].join(
      '###'
    );

    return simpleHash(minimalInfo);
  }
}

// 生成浏览器指纹hash值，并保存到所有可用的存储中
export const getBrowserFingerprint = async (): Promise<string> => {
  try {
    // 尝试从所有存储中获取已存在的指纹
    const existingFingerprint = getStorageValue(KEY_USER_FINGERPRINT);
    if (existingFingerprint) {
      return existingFingerprint;
    }

    // 获取所有特征信息
    const features = getBrowserFeatures();

    // 组合所有信息（不添加随机元素，确保同一浏览器生成的指纹一致）
    const info = features.join('###');

    // 使用SHA-1生成指纹（16位）
    let fingerprint: string;
    try {
      fingerprint = await sha1(info);
    } catch {
      // Crypto API 不可用（如非 HTTPS 环境），使用降级方案
      fingerprint = generateFallbackId(features);
    }

    // 最终验证：确保指纹不是空字符串（防御性编程）
    if (!fingerprint || fingerprint.trim() === '') {
      // 使用绝对最小特征集重新生成
      const emergencyInfo = [
        navigator.userAgent || 'unknown',
        navigator.language || 'unknown',
        `${screen.width}x${screen.height}`,
        navigator.platform || 'unknown',
      ].join('###');
      fingerprint = simpleHash(emergencyInfo);
    }

    // 保存到所有可用的存储中
    setStorageValue({
      key: KEY_USER_FINGERPRINT,
      value: fingerprint,
      syncToCookie: false,
    });

    return fingerprint;
  } catch {
    // 完全失败时，使用降级方案
    const features = getBrowserFeatures();
    let fallbackId = generateFallbackId(features);

    // 最终验证：确保降级ID也不是空字符串
    if (!fallbackId || fallbackId.trim() === '') {
      const emergencyInfo = [
        navigator.userAgent || 'unknown',
        navigator.language || 'unknown',
        `${screen.width}x${screen.height}`,
        navigator.platform || 'unknown',
      ].join('###');
      fallbackId = simpleHash(emergencyInfo);
    }

    // 尝试保存降级标识
    setStorageValue({
      key: KEY_USER_FINGERPRINT,
      value: fallbackId,
      syncToCookie: false,
    });

    return fallbackId;
  }
};
